import type {
  booleanData,
  contentChunkData,
  datetimeData,
  imagesData,
  numericData,
  paragraphCollectionData,
  pieceData,
  richTextData,
  selectionData,
  singleLineData,
} from "@aschehoug/crystallize-utils"
import type { InferOutput } from "valibot"
import {
  baseComponent,
  contentChunkComponent,
  multipleChoiceComponent,
  pieceComponent,
} from "@aschehoug/crystallize-utils"
import { array, fallback, intersect, nullish, object, pipe, string, transform, union } from "valibot"
import { convertToCase } from "../string"

export const componentContentSchema = union([
  baseComponent,
  contentChunkComponent,
  pieceComponent,
  multipleChoiceComponent,
  // TODO: Component choice
])

export const idSchema = object({ id: string() })

export const componentSchema = intersect([
  idSchema,
  componentContentSchema,
])

export type CrystallizeComponent = InferOutput<typeof componentSchema>

/**
 * Generic component schema which accepts an array of raw Crystallize components and then transforms them into a more usable format.
 *
 * Intended to be combined with more specific component schemas.
 */
export const genericComponents = pipe(
  fallback(array(fallback(nullish(componentSchema), null)), []),
  transform(it => it.filter(c => c !== null) as Array<CrystallizeComponent>),
  transform(transformComponents),
)

export type RawContent =
  | singleLineData.$output
  | booleanData.$output
  | richTextData.$output
  | richTextData.$output[number]
  | numericData.$output
  | selectionData.$output
  | datetimeData.$output
  | imagesData.$output
  | contentChunkData.$output
  | pieceData.$output
  | paragraphCollectionData.$output
  | paragraphCollectionData.$output[number]
  | { [key: string]: RawContent }
  | { type: string, value: RawContent | undefined }
  | Array<RawContent | undefined>
  | null

export type NormalizedComponents = Record<string, RawContent>

/**
 * Normalize a component's content by unwrapping it based on its type.
 *
 * @param component - Content part of a raw Crystallize component
 */
function getContent(component: CrystallizeComponent): RawContent | undefined {
  switch (component.type) {
    case "singleLine":
      return component.content?.text
    case "boolean":
      return component.content?.value
    case "richText":
      return component.content?.json ?? []
    case "numeric":
      return component.content?.number
    case "selection":
      return component.content?.options ?? []
    case "datetime":
      return component.content?.datetime
    case "images":
      return component.content?.images ?? []
    case "paragraphCollection":
      return component.content?.paragraphs ?? []
    case "contentChunk":
      return (component.content?.chunks ?? []).map(transformComponents)
    case "itemRelations":
      return component.content?.items ?? component.content?.productVariants ?? []
    case "piece":
      return transformComponents(component.content?.components ?? [])
    case "componentMultipleChoice":
      return (component.content?.selectedComponents ?? []).flatMap((c) => {
        const content = getContent(c)
        const arr = Array.isArray(content)
          ? content
          : [content]

        // FIXME: I don't like this. There should be a more generic solution, but currently necessary to be able to separate between the different possible components in a multiple choice block.
        return arr.map<{ type: string, value: RawContent | undefined }>(it => ({
          type: c.id,
          value: it,
        }))
      })
    default:
      return null
  }
}

/**
 * Transform an array of components to an object keyed by the components' IDs and with their contents normalized.
 *
 * The keys are also converted from kebab-case to camelCase for simplified lookup.
 *
 * @param components - Array of raw Crystallize components
 */
export function transformComponents(components: CrystallizeComponent[]) {
  return components.reduce<NormalizedComponents>((acc, component) => ({
    ...acc,
    [convertToCase(component.id, "camel")]: getContent(component) ?? null,
  }), {})
}
